import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { NodeViewWrapper, NodeViewContent, type NodeViewProps } from '@tiptap/react';
import { computePosition, autoUpdate, offset, flip, shift } from '@floating-ui/dom';
import { ChevronDown } from 'lucide-react';
import { LANGUAGE_OPTIONS, ensureLanguageLoaded } from '../extensions/codeLanguages';
import { sharedLowlight } from '../extensions/tiptapShared';
import { getRichTextOverlayRoot } from '../utils/overlayRoot';
import styles from './CodeBlockView.module.css';

const AUTO_DETECT_LABEL = 'Auto-detect';

export const CodeBlockView = ({ node, updateAttributes }: NodeViewProps) => {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const language: string | null = (node.attrs.language as string | null) ?? null;
  const currentLabel = LANGUAGE_OPTIONS.find(opt => opt.id === language)?.label ?? AUTO_DETECT_LABEL;

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const path = e.composedPath();
      if (triggerRef.current && path.includes(triggerRef.current)) return;
      if (menuRef.current && path.includes(menuRef.current)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDown, true);
    return () => document.removeEventListener('mousedown', onDown, true);
  }, [open]);

  useLayoutEffect(() => {
    if (!open) return;
    const referenceEl = buttonRef.current;
    const floatingEl = menuRef.current;
    if (!referenceEl || !floatingEl) return;

    const updatePosition = () => {
      computePosition(referenceEl, floatingEl, {
        placement: 'bottom-end',
        strategy: 'fixed',
        middleware: [offset(4), flip({ padding: 8 }), shift({ padding: 8 })]
      }).then(({ x, y, strategy }) => {
        Object.assign(floatingEl.style, { position: strategy, left: `${x}px`, top: `${y}px` });
      });
    };

    updatePosition();
    return autoUpdate(referenceEl, floatingEl, updatePosition, {
      ancestorScroll: true,
      ancestorResize: true,
      elementResize: true,
      layoutShift: true
    });
  }, [open]);

  const selectLanguage = (id: string | null) => {
    setOpen(false);
    if (id === null) {
      updateAttributes({ language: null });
      return;
    }
    ensureLanguageLoaded(sharedLowlight, id).then(() => updateAttributes({ language: id }));
  };

  return (
    <NodeViewWrapper className={styles.wrapper}>
      <div
        ref={triggerRef}
        className={`${styles.langControl} ${open ? styles.langControlOpen : ''}`}
        contentEditable={false}
      >
        <button
          ref={buttonRef}
          type="button"
          className={styles.langButton}
          tabIndex={-1}
          onMouseDown={e => {
            e.preventDefault();
            e.stopPropagation();
            setOpen(o => !o);
          }}
        >
          <span>{currentLabel}</span>
          <ChevronDown size={11} />
        </button>
      </div>
      {open &&
        createPortal(
          <div
            ref={menuRef}
            className={styles.langMenu}
            style={{ position: 'fixed', zIndex: 2147483000, pointerEvents: 'auto' }}
          >
            <button
              type="button"
              className={`${styles.langMenuItem} ${language === null ? styles.langMenuItemActive : ''}`}
              onMouseDown={e => {
                e.preventDefault();
                selectLanguage(null);
              }}
            >
              {AUTO_DETECT_LABEL}
            </button>
            {LANGUAGE_OPTIONS.map(opt => (
              <button
                key={opt.id}
                type="button"
                className={`${styles.langMenuItem} ${language === opt.id ? styles.langMenuItemActive : ''}`}
                onMouseDown={e => {
                  e.preventDefault();
                  selectLanguage(opt.id);
                }}
              >
                {opt.label}
              </button>
            ))}
          </div>,
          getRichTextOverlayRoot(buttonRef.current)
        )}
      <pre>
        <NodeViewContent as="code" />
      </pre>
    </NodeViewWrapper>
  );
};
